/**
 * SpatialGameLayout
 *
 * Main orchestrator component for the spatial campus navigation experience.
 * Renders location content, handles transitions, and integrates device-specific navigation.
 */

import React, { useCallback, useMemo } from 'react';
import { CampusLocation } from '../../types/spatial';
import { useSpatialNavigation } from '../../hooks/useSpatialNavigation';
import { useDeviceType } from '../../hooks/useDeviceType';
import { useSwipeNavigation } from '../../hooks/useSwipeNavigation';
import { useUnlockProgression } from '../../hooks/useUnlockProgression';
import { useAccessibleNavigation } from '../../hooks/useAccessibleNavigation';
import { TransitionContainer } from './transitions';
import { SpatialNavbar, MobileBottomNav, TabletSidebar } from './navigation';
import { LocationAnnouncer, SkipLink } from './accessibility';
import { getAdjacentLocations } from '../../config/locationConfig';

// Location Components
import { CampusOverview } from './CampusOverview/CampusOverview';
import { DesignStudioLocation } from '../locations/DesignStudioLocation';
import { ManufacturingLocation } from '../locations/ManufacturingLocation';
import { RDLabLocation } from '../locations/RDLabLocation';
import { SalesOfficeLocation } from '../locations/SalesOfficeLocation';
import { ExecutiveOfficeLocation } from '../locations/ExecutiveOfficeLocation';
import { HeadquartersLocation } from '../locations/HeadquartersLocation';
import { CompanyArchivesLocation } from '../locations/CompanyArchivesLocation';

import type { CompanyState } from '../../types/company';
import type { OwnedBuilding } from '../../types/buildings';
import type { Product, DesignConfig, ReviewSet } from '@smartphone-tycoon/shared-core';

interface GameSpeed {
  label: string;
  multiplier: number;
}

interface GameState {
  companyState: CompanyState;
  gameControls: {
    isPaused: boolean;
    gameSpeed: number;
  };
  events: unknown[];
  activeEvents?: unknown[];
  handleEventAction: (eventId: string, action: string) => void;
  onResearchStateChange?: (research: unknown) => void;
  updateCompanyState?: (updates: Partial<CompanyState>) => void;
  fireWarningEnabled?: boolean;
  onHire?: (role: string, count: number) => void;
  onFire?: (role: string, count: number) => void;
  onEmployeeUpdate?: (employees: unknown[]) => void;
  onHiringUpdate?: (processes: unknown[]) => void;
  onToggleFireWarning?: (show: boolean) => void;
  buildings?: unknown[];
  onBuildingsUpdate?: (buildings: unknown[]) => void;
  buildingManagement?: unknown;
}

interface ProductPipeline {
  products: Product[];
  actions: {
    startDevelopment: (
      designConfig: DesignConfig,
      cash: number,
      updateCash: (newCash: number) => void
    ) => Promise<{ success: boolean; error?: string }>;
    startProduction: (
      productId: string,
      gameDate: { day: number; month: number; year: number }
    ) => Promise<{ success: boolean; error?: string }>;
    discontinueProduct: (productId: string) => Promise<{ success: boolean; error?: string }>;
    updateProductSales: (
      productSales: Array<{ productId: string; unitsSold: number; revenue: number }>
    ) => void;
    setProducts: (products: Product[]) => void;
  };
  utils?: {
    getPhaseProgress: (product: Product) => {
      progress: number;
      daysRemaining: number;
      totalDays: number;
    };
    isPhaseComplete: (product: Product) => boolean;
    getProductAllocations?: () => unknown[];
    isProductStagePaused?: (productId: string) => boolean;
  };
}

export interface SpatialGameLayoutProps {
  gameState: GameState;
  productPipeline: ProductPipeline;
  onBack: () => void;
  companyName?: string;
  ceoName?: string;
  onProductAction?: (productId: string, action: string) => void;
  onTogglePause?: () => void;
  onChangeSpeed?: (speed: GameSpeed) => void;
  availableSpeeds?: GameSpeed[];
  releaseModalOpen?: boolean;
  productToRelease?: Product | null;
  productReviews?: ReviewSet | null;
  onReleaseModalChange?: (
    open: boolean,
    product: Product | null,
    reviews: ReviewSet | null
  ) => void;
  discontinueModalOpen?: boolean;
  productToDiscontinue?: Product | null;
  onDiscontinueModalChange?: (open: boolean, product: Product | null) => void;
}

export const SpatialGameLayout: React.FC<SpatialGameLayoutProps> = ({
  gameState,
  productPipeline,
  onBack,
  companyName,
  ceoName,
  onProductAction,
  onTogglePause,
  onChangeSpeed,
  availableSpeeds,
  releaseModalOpen,
  productToRelease,
  productReviews,
  onReleaseModalChange,
  discontinueModalOpen,
  productToDiscontinue,
  onDiscontinueModalChange,
}) => {
  const { currentLocation, navigateTo, isAtCampus, isUnlocked } = useSpatialNavigation();
  const { isMobile, isTablet } = useDeviceType();

  // Initialize unlock progression system
  useUnlockProgression();

  // Speed control helpers for keyboard shortcuts
  const handleSpeedUp = useCallback(() => {
    if (!availableSpeeds || !onChangeSpeed) return;
    const speeds = availableSpeeds.filter(s => s.multiplier > 0);
    const currentIdx = speeds.findIndex(s => s.multiplier === gameState.gameControls.gameSpeed);
    const nextIdx = Math.min(currentIdx + 1, speeds.length - 1);
    if (gameState.gameControls.isPaused && onTogglePause) onTogglePause();
    onChangeSpeed(speeds[nextIdx]);
  }, [availableSpeeds, onChangeSpeed, gameState.gameControls, onTogglePause]);

  const handleSpeedDown = useCallback(() => {
    if (!availableSpeeds || !onChangeSpeed) return;
    const speeds = availableSpeeds.filter(s => s.multiplier > 0);
    const currentIdx = speeds.findIndex(s => s.multiplier === gameState.gameControls.gameSpeed);
    const prevIdx = Math.max(currentIdx - 1, 0);
    onChangeSpeed(speeds[prevIdx]);
  }, [availableSpeeds, onChangeSpeed, gameState.gameControls]);

  // Initialize keyboard navigation with speed controls
  useAccessibleNavigation({
    onTogglePause,
    onSpeedUp: handleSpeedUp,
    onSpeedDown: handleSpeedDown,
  });

  // Swipe navigation for mobile
  const handleSwipeNavigation = useCallback(
    (direction: 'left' | 'right') => {
      if (isAtCampus) return;

      const adjacentLocations = getAdjacentLocations(currentLocation);
      const unlockedAdjacent = adjacentLocations.filter((loc) => isUnlocked(loc));

      if (unlockedAdjacent.length === 0) return;

      // Simple left/right navigation through adjacent locations
      // In practice, you might want smarter directional logic
      const index = direction === 'right' ? 0 : Math.min(1, unlockedAdjacent.length - 1);
      if (unlockedAdjacent[index]) {
        navigateTo(unlockedAdjacent[index]);
      }
    },
    [currentLocation, isAtCampus, isUnlocked, navigateTo]
  );

  // Enable swipe navigation on mobile
  useSwipeNavigation({
    enabled: isMobile && !isAtCampus,
    onSwipeLeft: () => handleSwipeNavigation('left'),
    onSwipeRight: () => handleSwipeNavigation('right'),
  });

  // Memoize common props for location components
  const locationProps = useMemo(
    () => ({
      gameState,
      productPipeline,
      companyName,
      ceoName,
      onProductAction,
      releaseModalOpen,
      productToRelease,
      productReviews,
      onReleaseModalChange,
      discontinueModalOpen,
      productToDiscontinue,
      onDiscontinueModalChange,
    }),
    [
      gameState,
      productPipeline,
      companyName,
      ceoName,
      onProductAction,
      releaseModalOpen,
      productToRelease,
      productReviews,
      onReleaseModalChange,
      discontinueModalOpen,
      productToDiscontinue,
      onDiscontinueModalChange,
    ]
  );

  /**
   * Render the content for the current location
   */
  const renderLocationContent = () => {
    switch (currentLocation) {
      case CampusLocation.CAMPUS_OVERVIEW:
        return <CampusOverview />;

      case CampusLocation.HEADQUARTERS:
        return (
          <HeadquartersLocation
            companyState={gameState.companyState}
            companyName={companyName ?? ''}
            products={productPipeline.products}
            employees={gameState.companyState?.employees ?? []}
            hiringProcesses={gameState.companyState?.hiringProcesses ?? []}
            employeeAllocations={gameState.companyState?.employeeAllocations ?? []}
            showFireWarning={gameState.fireWarningEnabled}
            onHire={gameState.onHire}
            onFire={gameState.onFire}
            onToggleFireWarning={gameState.onToggleFireWarning}
            onStateUpdate={gameState.updateCompanyState}
          />
        );

      case CampusLocation.DESIGN_STUDIO:
        return (
          <DesignStudioLocation
            products={productPipeline.products}
            gameDate={{
              day: gameState.companyState.day,
              month: gameState.companyState.month,
              year: gameState.companyState.year
            }}
            companyState={gameState.companyState}
            companyName={companyName ?? ''}
            ceoName={ceoName}
            onCreateProduct={async (config: DesignConfig) => {
              const result = await productPipeline.actions.startDevelopment(
                config,
                gameState.companyState.cash,
                (newCash) => gameState.updateCompanyState?.({ cash: newCash })
              );
              return result;
            }}
            onProductAction={onProductAction}
            releaseModalOpen={releaseModalOpen}
            productToRelease={productToRelease}
            productReviews={productReviews}
            onReleaseModalChange={onReleaseModalChange}
            discontinueModalOpen={discontinueModalOpen}
            productToDiscontinue={productToDiscontinue}
            onDiscontinueModalChange={onDiscontinueModalChange}
            getPhaseProgress={productPipeline.utils?.getPhaseProgress}
          />
        );

      case CampusLocation.MANUFACTURING:
        return (
          <ManufacturingLocation
            companyState={gameState.companyState}
            products={productPipeline.products}
            employees={gameState.companyState?.employees ?? []}
            hiringProcesses={gameState.companyState?.hiringProcesses ?? []}
            employeeAllocations={gameState.companyState?.employeeAllocations ?? []}
            showFireWarning={gameState.fireWarningEnabled}
            onHire={gameState.onHire}
            onFire={gameState.onFire}
            onToggleFireWarning={gameState.onToggleFireWarning}
            buildings={gameState.buildings as OwnedBuilding[] | undefined}
            onBuildingsUpdate={gameState.onBuildingsUpdate as ((buildings: OwnedBuilding[]) => void) | undefined}
            onCashUpdate={(newCash) => gameState.updateCompanyState?.({ cash: newCash })}
            onStateUpdate={gameState.updateCompanyState}
          />
        );

      case CampusLocation.RD_LAB:
        return (
          <RDLabLocation
            gameState={locationProps.gameState}
          />
        );

      case CampusLocation.SALES_OFFICE:
        return (
          <SalesOfficeLocation
            companyState={gameState.companyState}
            companyName={companyName ?? ''}
            ceoName={ceoName}
            products={productPipeline.products}
          />
        );

      case CampusLocation.EXECUTIVE_OFFICE:
        return (
          <ExecutiveOfficeLocation
            companyState={gameState.companyState}
            products={productPipeline.products}
          />
        );

      case CampusLocation.COMPANY_ARCHIVES:
        return <CompanyArchivesLocation />;

      default:
        return (
          <div className="flex items-center justify-center h-full">
            <p className="text-theme-text-muted">Unknown location</p>
          </div>
        );
    }
  };

  // Calculate main content padding based on device
  const contentPadding = useMemo(() => {
    if (isTablet) return 'pl-16'; // Account for collapsed sidebar
    if (isMobile) return 'pb-16'; // Account for bottom nav
    return '';
  }, [isTablet, isMobile]);

  // Campus overview: full-screen 3D with floating overlays
  // Building interiors: standard grid layout with navbar + content
  if (isAtCampus) {
    return (
      <div className="fixed inset-0 overflow-hidden min-w-[1024px] min-h-[600px]">
        {/* Accessibility Features */}
        <SkipLink targetId="main-content" />
        <LocationAnnouncer />

        {/* Full-screen campus scene */}
        <div id="main-content" tabIndex={-1} className="w-full h-full">
          <TransitionContainer currentLocation={currentLocation} className="h-full">
            {renderLocationContent()}
          </TransitionContainer>
        </div>

        {/* Floating navbar overlay */}
        <div className="absolute top-0 left-0 right-0 z-10 p-4">
          <SpatialNavbar
            companyState={gameState.companyState}
            gameControls={gameState.gameControls}
            companyName={companyName}
            onBack={onBack}
            onTogglePause={onTogglePause}
            onChangeSpeed={onChangeSpeed}
            availableSpeeds={availableSpeeds}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen overflow-hidden bg-theme-bg-secondary">
      {/* Accessibility Features */}
      <SkipLink targetId="main-content" />
      <LocationAnnouncer />

      {/* Tablet Sidebar */}
      {isTablet && <TabletSidebar defaultCollapsed />}

      {/* Main Content Area */}
      <div
        className={`w-full h-full grid grid-rows-[80px_1fr] gap-4 p-4 ${contentPadding}`}
      >
        {/* Top Navigation Bar */}
        <SpatialNavbar
          companyState={gameState.companyState}
          gameControls={gameState.gameControls}
          companyName={companyName}
          onBack={onBack}
          onTogglePause={onTogglePause}
          onChangeSpeed={onChangeSpeed}
          availableSpeeds={availableSpeeds}
        />

        {/* Location Content with Transitions */}
        <div
          id="main-content"
          tabIndex={-1}
          className="bg-theme-bg-content border border-theme-border rounded-lg overflow-hidden"
        >
          <TransitionContainer
            currentLocation={currentLocation}
            className="h-full"
          >
            {renderLocationContent()}
          </TransitionContainer>
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      {isMobile && !isAtCampus && <MobileBottomNav />}
    </div>
  );
};

export default SpatialGameLayout;
